import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Validate from 'validate.js';
import { InputData } from './data/input-data';
import { InputError } from './data/input-error';
import get from 'lodash/get';
import { injectIntl } from 'react-intl';
import classNames from 'classnames';

class Input extends Component {
  constructor (props) {
    super(props);

    const { inputFieldName = '' } = this.props;
    const { values, error } = InputData[inputFieldName];

    this.state = {
      error: error,
      inputColSize: null,
      isValid: false,
      values: values
    };

    this.clearValue = this.clearValue.bind(this);
    this.isValid = this.isValid.bind(this);
    this.getValue = this.getValue.bind(this);
    this.validateCheckbox = this.validateCheckbox.bind(this);
    this.inputErrorFn = this.inputErrorFn.bind(this);
    this.optionalState = this.optionalState.bind(this);
    this.validState = this.validState.bind(this);
    this.invalidState = this.invalidState.bind(this);
    this.invalidCbState = this.invalidCbState.bind(this);
    this.checkForHelperText = this.checkForHelperText.bind(this);
    this.checkForErrorMsg = this.checkForErrorMsg.bind(this);
    this.adjustInputSize = this.adjustInputSize.bind(this);
    this.checkForLabel = this.checkForLabel.bind(this);
    this.checkForIcon = this.checkForIcon.bind(this);
    this.checkInputType = this.checkInputType.bind(this);
    this.checkForAddOn = this.checkForAddOn.bind(this);
    this.onChange = this.onChange.bind(this);
  }

  componentDidMount() {
    if (this.props.instantCheckForError) {
      this.isValid();
    }
  }

  // TODO: Improve for all cases, not just standard input
  clearValue () {
    const input = this.refs.inputField;
    if (input.value) {
      input.value = input.defaultVal || '';
    }
    this.optionalState();
  }

  isValid (ignoreValidation = false) {
    let errorFree = true;
    const inputFieldName = this.props.inputFieldName;
    const errorData = InputError[inputFieldName].error;
    const constraints = errorData.constraints;
    const customConstraints = this.props.customConstraints;
    const input = this.refs.inputField;
    if ('checkbox' === input.type) {
      this.validateCheckbox();
    }

    if (customConstraints) {
      Validate.validators.custom = customConstraints;
    }

    const val = input.value;
    const isError = Validate.single(val, !ignoreValidation ? constraints : {});
    const optional = this.props.optional;
    if (!this.props.validFn) {
      this.inputErrorFn(isError, inputFieldName, val);
    } else {
      this.props.validFn();
    }

    if (isError && (!optional || val)) {
      errorFree = false;
    }

    return errorFree;
  }

  getValue () {
    const input = this.refs.inputField;
    const val = input.value;
    return val;
  }

  validateCheckbox () {
    const checkbox = this.refs.inputField;
    const checked = checkbox.checked;
    if (checked) {
      checkbox.value = true;
    } else {
      checkbox.value = '';
    }
  }

  inputErrorFn (isError, inputFieldName, val) {
    const errorData = InputError[inputFieldName].error;
    const optional = this.props.optional;

    if (isError) {
      if (optional && !val) {
        // the error msg that comes back for an optional field that is blank
        // is a string that says "optional", since it is optional this field
        // will return to its default UI and set isValid to true
        this.optionalState(errorData);
      } else {
        // will change UI of input field to an error state and
        // set isValid to false
        this.invalidState(errorData, isError);
      }
    } else {
      if (this.props.actionFn) {
        const user = {};
        let value = this.refs.inputField.value;
        if ('checkbox' === this.refs.inputField.type) {
          value = this.refs.inputField.checked;
        }
        user[this.props.elementID] = value;
        this.props.actionFn(user, (...args) => {
          this.props.actionCb(
            errorData,
            this.validState,
            this.invalidCbState,
            ...args);
          // set state to isValid to true or false depending on
          // response of callback
          // if error will output error msg and display error state UI
        });
      } else {
        this.validState(errorData);
      }
    }
  }

  optionalState () {
    const errorObj = {};
    errorObj.inputBorder = null;
    errorObj.errorMsg = null;
    this.setState({ isValid: true });
    this.setState({ error: errorObj });
  }

  validState (errorData) {
    const errorObj = {};
    errorObj.inputBorder = null;
    errorObj.errorMsg = null;
    this.setState({ isValid: true });
    this.setState({ error: errorObj });
  }

  invalidState (errorData, isError) {
    const errorObj = {};
    errorObj.inputBorder = errorData.inputBorder;
    errorObj.errorMsg = isError[0];
    this.setState({ isValid: false });
    this.setState({ error: errorObj });
  }

  invalidCbState (errorData) {
    const errorObj = {};
    errorObj.inputBorder = errorData.inputBorder;
    this.setState({ isValid: false });
    this.setState({ error: errorObj });
  }

  checkForHelperText (value) {
    const helperText = this.props.helperText || value.helper_text;
    if (helperText) {
      return <div className={"help-block text-left info-text"}>{helperText}</div>;
    }
  }

  checkForErrorMsg (errors) {
    if (errors.errorMsg) {
      const { errorMessage, intl } = this.props;

      // use custom error message if defined
      if (errorMessage) {
        this.state.error.errorMsg = errorMessage;
      }

      const intlError = get(this.state, 'error.errorMsg.id') ? intl.formatMessage(this.state.error.errorMsg) : this.state.error.errorMsg;
      return (
        <div className="help-block inline-help text-danger text-left">
          {intlError}
        </div>
      );
    }
  }

  adjustInputSize (labelSize) {
    const splitCols = labelSize.split('-');
    const colDifference = 12 - splitCols[2];
    if (0 === colDifference) {
      return;
    }
    const inputColSize = `${splitCols[0]}-${splitCols[1]}-${colDifference}`;
    this.setState({ inputColSize });
  }

  checkForLabel (value) {
    const { labelSize, customLabel } = this.props;
    if (labelSize) {
      this.adjustInputSize(labelSize);
      return <label htmlFor={value.id} className={`${labelSize} control-label text-left`}>{this.props.label || value.label}</label>;
    }
    if (customLabel) {
      return customLabel;
    }
  }

  checkForIcon () {
    let icon = '';
    const caret = 'select' === get(this.state, 'values.type') ? <span style={{ fontSize: 11, top: '3px', position: 'relative', marginLeft: '6px', marginRight: '4px' }}className="icon-arrow-nav"></span> : '';
    if (!this.props.optional && !this.props.hideRequired) {
      icon = <span className="required">*</span>;
    }
    return (<span className={"form-control-feedback"}>
      {icon}
      {caret}
    </span>);
  }

  checkInputType (value, errors) {
    const { dataAttributes, intl, ignoreTextAreaBlurValidation = false } = this.props;
    const { props } = this;
    const elementID = props.elementID || value.id;
    const valuePlaceholder = get(value, 'placeholder.id') ? intl.formatMessage(value.placeholder) : value.placeholder;

    if ('textarea' === value.type) {
      return (<textarea {...dataAttributes} maxLength={value.maxLength} ref="inputField"
        className={`form-textarea form-control rr-block ${(errors.inputBorder || '')}`}
        id={elementID}
        placeholder={props.placeholder || valuePlaceholder}
        onBlur={()=>this.isValid(ignoreTextAreaBlurValidation)}
        defaultValue={props.defaultVal}
        disabled={props.disabled}
      ></textarea>);
    }
    if ('checkbox' === value.type) {
      return (
        <div className="CheckboxField custom-control custom-checkbox">
          <input
            {...dataAttributes }
            ref="inputField"
            id={elementID}
            type="checkbox"
            onChange={this.isValid}
            disabled={props.disabled}
            defaultChecked={props.defaultVal}
            className="custom-control-input"
            name={props.name || elementID || ''}
          />
          <label
            className="custom-control-label flex"
            htmlFor={elementID}
          >
            <span className="checkbox-ui"></span>
            {value.label}
          </label>
        </div>
      );
    }

    if ('select' === value.type) {
      const { options, preSelect, defaultText } = props;
      const color = preSelect ? '' : '#CED2DA';

      const onSelect = function (e) {
        const event = e;
        const colorAttribute = get(event, 'currentTarget.style.color');
        if (colorAttribute) event.currentTarget.style.color = '#343840';
      };

      return (
        <div>
          <select name={props.name || elementID || ''} onChange={onSelect} onBlur={this.isValid} defaultValue={preSelect} style={{ color }} id={elementID} ref="inputField" className={`form-control ${(errors.inputBorder || '')}`}>
            <option className="select-default-text" value="" disabled selected>{defaultText || 'Select'}</option>
            { options.map((option) => {
              const { val, text } = option;
              const info = {
                value: val
              };
              return <option {...info}>{text || value}</option>;
            })}
          </select>
        </div>
      );
    }

    return (
      <input
        {...dataAttributes}
        min={this.props.min}
        max={this.props.max}
        maxLength={value.maxLength}
        ref="inputField"
        name={props.name || elementID || ''}
        type={value.type || 'text'}
        className={`form-control rr-block ${(errors.inputBorder || '')}`}
        id={this.props.elementID || value.id}
        placeholder={this.props.placeholder || valuePlaceholder}
        onBlur={this.isValid}
        onChange={this.onChange}
        defaultValue={this.props.defaultVal || ''}
        value={ this.props.setVal ? this.props.setVal : undefined }
        disabled={this.props.disabled}
      />
    );
  }

  checkForAddOn (value, errors) {
    const { addOn } = this.props;
    if (addOn) {
      return (
        <div className="input-group">
          <div className="input-group-addon">{addOn}</div>
          { this.checkInputType(value, errors) }
        </div>
      );
    }
    return this.checkInputType(value, errors);
  }

  onChange (e) {
    if (this.props.onChange) {
      this.props.onChange(e);
    }
  }

  render () {
    const value = this.state.values;
    const errors = this.state.error;
    return (
      <div
        className={classNames(this.props.colSize, {
          'col-xs-12': this.props.useBootstrapGrid && !this.props.colSize
        })}
      >
        <div
          className="form-group has-feedback"
          style={
            this.props.useBootstrapGrid
              ? {}
              : {
                  marginRight: 0,
                  marginLeft: 0
                }
          }
        >
          { this.checkForLabel(value) }
          <div className={classNames('relative', this.props.inputColSize, {
            'col-xs-12': this.props.useBootstrapGrid && !this.props.inputColSize
          })}>
            { this.checkForAddOn(value, errors) }
            { this.checkForIcon(errors) }
            { this.checkForErrorMsg(errors) }
            { this.checkForHelperText(value) }
          </div>
        </div>
      </div>
    );
  }
}

export const InputField = injectIntl(Input, { withRef: true, forwardRef: true });

Input.defaultProps = {
  optional: true,
  dataAttributes: {},
  disabled: false,
  useBootstrapGrid: true
};

Input.propTypes = {
  elementID: PropTypes.string,
  labelSize: PropTypes.string, // renders Size
  label: PropTypes.string, // renders label text from Form data
  colSize: PropTypes.string,
  optional: PropTypes.bool, // determines whether input is optional
  hideRequired: PropTypes.bool, // determines whether required is shown
  dataAttributes: PropTypes.object, // attributes for the input i.e. { data-stripe: 'bank-account'}
  inputFieldName: PropTypes.oneOf(
    ['email',
      'password',
      'terms_of_service',
      'first_name',
      'last_name',
      'address1',
      'address2',
      'city',
      'state',
      'zipcode',
      'organization',
      'mobile_phone',
      'job_title',
      'verification_code',
      'login_pw',
      'required_textbox_response',
      'review_response',
      'msg_host',
      'flex_time',
      'attendees',
      'cardholders_name',
      'cardnumber',
      'month',
      'year',
      'day',
      'cvv',
      'default_input',
      'default_input_number',
      'default_textarea',
      'account_number',
      'routing_number',
      'hidden_country_code',
      'hidden_currency_code',
      'account_holder',
      'hidden_account_type',
      'tax_id',
      'business_name',
      'last_4',
      'board_name',
      'board_description',
      'activity',
      'activity_date',
      'location',
      'meta_email',
      'personal_message',
      'concierge_budget',
      'other_concierge_items',
      'concierge_details',
      'comment',
      'other_how_did_you_hear',
      'how_did_you_hear',
      'block_day_title',
      'csrf_token_input',
      'transit_number',
      'institution_number',
      'account_number_ca',
      'hidden_routing_number',
      'account_number_uk',
      'sort_code',
      'all_day',
      'bsb_number',
      'account_number_au',
      'iban_number',
      'custom_offer_subtotal'
    ]
  ).isRequired, // keys in input-data.js
  validFn: PropTypes.func,
  actionFn: PropTypes.func,
  actionCb: PropTypes.func,
  defaultVal: PropTypes.string,
  placeholder: PropTypes.string,
  helperText: PropTypes.string,
  errorMessage: PropTypes.string,
  customConstraints: PropTypes.func,
  onChange: PropTypes.func,
  instantCheckForError: PropTypes.bool,
  ignoreTextAreaBlurValidation: PropTypes.bool,
  useBootstrapGrid: PropTypes.bool,
};
